<template>
  <div class="mx-4 flex flex-col gap-2">
    <section class="flex w-full flex-wrap items-center justify-between gap-2">
      <div class="flex flex-wrap items-center gap-2">
        <ButtonTertiary
          v-if="current_profile.type !== PROFILE_TYPES.CUSTOMER"
          action="filter"
          data-test="notifications-filter"
          :hide_text="!!mq.isMobile"
          @onClick="toggle_filter = !toggle_filter"
        >
          <span>{{ $t('notification.labels.filter') }}</span>
        </ButtonTertiary>

        <ButtonTertiary
          action="sort"
          data-test="notification-sorting-dropdown"
          :hide_text="!!mq.isMobile"
          @onClick="toggle_popover"
        >
          {{ $t(`sorting.${sorting_label}`) }}
        </ButtonTertiary>

        <Popover ref="popover">
          <div class="flex flex-col p-1">
            <div
              class="cursor-pointer rounded px-4 py-2 text-sm hover:bg-surface-100"
              data-test="sort-updated-at-desc"
              @click="$emit('set_sort_order', ['updated_at desc'])"
            >
              {{ $t('sorting.updated_at_desc') }}
            </div>
            <div
              class="cursor-pointer rounded px-4 py-2 text-sm hover:bg-surface-100"
              data-test="sort-updated-at-asc"
              @click="$emit('set_sort_order', ['updated_at asc'])"
            >
              {{ $t('sorting.updated_at_asc') }}
            </div>

            <div
              class="cursor-pointer rounded px-4 py-2 text-sm hover:bg-surface-100"
              data-test="sort-level-name-asc"
              @click="$emit('set_sort_order', ['notification_level_type desc', 'notification_level_name asc'])"
            >
              {{ is_sup ? $t('sorting.notification_level_name_asc_sup') : $t('sorting.notification_level_name_asc') }}
            </div>

            <div
              class="cursor-pointer rounded px-4 py-2 text-sm hover:bg-surface-100"
              data-test="sort-level-name-desc"
              @click="$emit('set_sort_order', ['notification_level_type desc', 'notification_level_name desc'])"
            >
              {{ is_sup ? $t('sorting.notification_level_name_desc_sup') : $t('sorting.notification_level_name_desc') }}
            </div>
          </div>
        </Popover>
      </div>

      <label
        class="flex size-10 cursor-pointer flex-col items-center justify-center rounded-full bg-surface-100 text-sm hover:bg-surface-200"
        @click="toggle_menu"
      >
        <FontAwesomeIcon
          aria-controls="overlay_menu"
          aria-haspopup="true"
          :icon="['far', 'ellipsis']"
          size="lg"
        />
      </label>
      <PMenu
        id="overlay_menu"
        ref="menu"
        :model="items"
        :popup="true"
      >
        <template #item="{ item, props }">
          <div
            v-bind="props.action"
            @click.stop="$emit('open_notification_subgroups_all')"
          >
            <div class="text-sm">
              {{ item.label }}
            </div>
          </div>
        </template>
      </PMenu>
    </section>

    <section
      v-if="current_profile.type === PROFILE_TYPES.EMPLOYEE"
      class="flex grow flex-wrap items-center gap-2"
    >
      <FilterTag
        v-if="q_project_id && project_selected"
        data-test="project-tag-filter"
        size="is-small"
        @close="q_project_id = null"
      >
        {{ $truncate(project_selected.attributes.name, 20) }}
      </FilterTag>
      <FilterTag
        v-if="unit_selected"
        data-test="unit-tag-filter"
        size="is-small"
        @close="[(q_unit_id = null)]"
      >
        {{ $truncate(unit_selected.attributes.name, 20) }}
      </FilterTag>
      <FilterTag
        v-if="q_decision_type_id && decision_type_selected"
        data-test="decisions-tag-filter"
        size="is-small"
        @close="q_decision_type_id = null"
      >
        {{ $truncate(decision_type_selected.attributes.name, 20) }}
      </FilterTag>
      <FilterTag
        v-if="q_is_unread_only"
        data-test="unread-only-tag-filter"
        size="is-small"
        @close="q_is_unread_only = false"
      >
        {{ $t('notification.labels.unreadOnly') }}
      </FilterTag>
    </section>

    <section v-if="toggle_filter">
      <div class="relative mb-4">
        <div class="mb-4">
          <label class="mb-1 block text-sm font-medium text-gray-700">{{ $t('notification.labels.status') }}</label>
          <div class="flex gap-2">
            <ToggleToken
              data-test="show-unopen-notifications"
              :is_toggled="q_is_unread_only"
              @onClick="q_is_unread_only = !q_is_unread_only"
            >
              {{ $t('notification.labels.unreadOnly') }}
            </ToggleToken>
          </div>
        </div>

        <div
          v-if="current_profile.type === PROFILE_TYPES.EMPLOYEE"
          class="flex w-full gap-2"
        >
          <div class="flex-1">
            <label class="mb-1 block text-sm font-medium text-gray-700">{{ $t('ticket.labels.project') }}</label>
            <SelectProject
              :active="project_selected"
              @changeSelect="(p) => (q_project_id = _.get(p, 'id'))"
            />
          </div>
          <div
            class="flex-1"
            v-if="!is_sup"
          >
            <label class="mb-1 block text-sm font-medium text-gray-700">{{ $t('ticket.labels.unit') }}</label>
            <SelectUnit
              :active="unit_selected"
              :disabled="!selectable_units"
              :project_id="q_project_id"
              @changeSelect="(u) => (q_unit_id = _.get(u, 'id'))"
            />
          </div>
        </div>
        <div
          v-if="
            !show_extra_filters &&
            $flipper.enabled($FT.DECISIONS_CORE) &&
            current_profile.type === PROFILE_TYPES.EMPLOYEE
          "
          class="mt-2 flex justify-end"
        >
          <a
            data-test="show-extra-filters"
            @click.stop="show_extra_filters = true"
          >
            {{ $t('notification.labels.showExtraFilters') }}
          </a>
        </div>

        <div
          v-else-if="show_extra_filters"
          class="mt-4 w-full"
        >
          <div class="mb-1 flex items-center justify-end gap-2">
            <span class="font-normal">
              <a @click.stop="[(show_extra_filters = false), (q_decision_type_id = null)]">
                {{ $t('notification.labels.hideExtraFilters') }}
              </a>
            </span>
          </div>
          <div v-if="$flipper.enabled($FT.DECISIONS_CORE)">
            <label class="mb-1 block text-sm font-medium text-gray-700">{{ $t('decision.labels.decisions') }}</label>
            <SelectDecisionType
              :model-value="decision_type_selected"
              :project_id="q_project_id"
              :unit_id="q_unit_id"
              @changeSelect="
                (dt) => {
                  q_decision_type_id = _.get(dt, 'id');
                }
              "
            />
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script setup>
import { computed, inject, onMounted, ref, watch } from 'vue';
import _ from 'lodash-es';
import { useStore } from 'vuex';
import { useMqService } from '@/plugins/mq';
import { PROFILE_TYPES, PROJECT_TYPES } from '@/app/data/model_constants';
import { SIDEBAR_TYPE_NOTIFICATIONS } from '@/app/data/general_constants';
import { FT } from '@/app/data/flipper_constants';
import ToggleToken from '@/app/shared_components/buttons/ToggleToken.vue';
import FilterTag from '@/app/shared_components/tags/FilterTag.vue';
import SelectProject from '@/app/shared_components/selects/SelectProject.vue';
import SelectUnit from '@/app/shared_components/selects/SelectUnit.vue';
import SelectDecisionType from '@/app/shared_components/selects/SelectDecisionType.vue';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  sort_order: { type: String, required: true },
  sort_order_type: { type: String, required: true },
});

const emit = defineEmits(['set_sort_order', 'fetch_notification_subgroups', 'open_notification_subgroups_all']);

const store = useStore();
const mq = useMqService();
const { t } = useI18n();
const $flipper = inject('flipper');
const $FT = FT;

const initialized = ref(false);
const q_is_unread_only = ref(false);
const q_project_id = ref(null);
const q_unit_id = ref(null);
const q_decision_type_id = ref(null);
const toggle_filter = ref(false);
const show_extra_filters = ref(false);
const popover = ref(null);
const menu = ref(null);

const items = computed(() => [
  {
    label: t('notification.actions.markAllAsRead'),
  },
]);

const current_client = computed(() => store.getters.current_client);
const current_profile = computed(() => store.getters.current_profile);
const decision_types = computed(() => store.getters.decision_types);
const sidebar_prefs = computed(() => store.getters.sidebar_prefs);
const projects = computed(() => store.getters.projects);
const sidebar_type = computed(() => store.getters.sidebar_type);
const units = computed(() => store.getters.units);

const selectable_units = computed(() => !!q_project_id.value);

const filters = computed(() => {
  const decision_type_id = q_decision_type_id.value;
  const project_id = q_project_id.value;
  const unread = q_is_unread_only.value;
  const unit_id = q_unit_id.value;
  return {
    decision_type_id,
    project_id,
    unit_id,
    unread,
  };
});

const project_selected = computed(() => {
  if (!q_project_id.value) return null;
  return _.find(projects.value, ['id', q_project_id.value]);
});

const is_sup = computed(() => current_client.value.attributes.project_type === PROJECT_TYPES.SUP);

const unit_selected = computed(() => {
  if (!q_unit_id.value) return null;
  return _.find(units.value, ['id', q_unit_id.value]);
});

const decision_type_selected = computed(() => {
  if (!q_decision_type_id.value) return null;
  return _.find(decision_types.value, ['id', q_decision_type_id.value]);
});

const sorting_label = computed(() => [props.sort_order_type, props.sort_order].join('_'));

const FETCH_DECISION_TYPE = (payload) => store.dispatch('FETCH_DECISION_TYPE', payload);
const UPDATE_SIDEBAR_PREFS = (payload) => store.dispatch('UPDATE_SIDEBAR_PREFS', payload);
const FETCH_PROJECT = (payload) => store.dispatch('FETCH_PROJECT', payload);
const FETCH_UNIT = (payload) => store.dispatch('FETCH_UNIT', payload);

const toggle_popover = (event) => {
  popover.value.toggle(event);
};

const toggle_menu = (event) => {
  menu.value.toggle(event);
};

const fetch_filters_records = async () => {
  if (q_decision_type_id.value) await FETCH_DECISION_TYPE({ id: q_decision_type_id.value });
  if (q_project_id.value) await FETCH_PROJECT({ id: q_project_id.value });
  if (q_unit_id.value) await FETCH_UNIT({ id: q_unit_id.value });
};

watch(filters, async () => {
  await fetch_filters_records();
  UPDATE_SIDEBAR_PREFS(filters.value);
  emit('fetch_notification_subgroups');
});

watch(sidebar_type, async (value) => {
  if (value === SIDEBAR_TYPE_NOTIFICATIONS && !initialized.value) {
    await fetch_filters_records();
    initialized.value = true;
  }
});

onMounted(() => {
  q_project_id.value = sidebar_prefs.value.project_id;
  q_decision_type_id.value = sidebar_prefs.value.decision_type_id;
  q_is_unread_only.value = sidebar_prefs.value.unopen;
  q_unit_id.value = sidebar_prefs.value.unit_id;
});
</script>
